import React, { useState, useEffect } from 'react';
import { Box, Tooltip, IconButton, Badge, Menu, MenuItem, Divider, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import PauseIcon from '@mui/icons-material/Pause';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CampaignIcon from '@mui/icons-material/Campaign';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CloseIcon from '@mui/icons-material/Close';
import tutorialSystem from './utils/TutorialSystem';
import nudgesSystem from './utils/NudgesSystem';
import clickTracker from './utils/ClickTracker';
import PrismClickHistoryModal from './PrismClickHistoryModal';

export default function PrismStatusBar({ 
  sessionTime = 0, 
  sectionsVisited = 0, 
  complianceScore = 0,
  featuresEnabled = {},
  onOpenDocAssistant,
  onOpenProtocolSearch 
}) {
  // Role switching state
  const [currentRole, setCurrentRole] = useState('coordinator'); // 'coordinator', 'team_lead', 'admin'
  const [roleMenuAnchor, setRoleMenuAnchor] = useState(null);
  
  const [helpRequestStatus, setHelpRequestStatus] = useState(null); // null, 'pending', 'acknowledged'
  const [nudgesCount, setNudgesCount] = useState(0);
  const [showNudges, setShowNudges] = useState(false);
  const [updatesAnchor, setUpdatesAnchor] = useState(null);
  const [nudgesAnchor, setNudgesAnchor] = useState(null);
  const [clickCount, setClickCount] = useState(0);
  const [clickHistoryOpen, setClickHistoryOpen] = useState(false);
  const [clickHistoryAnchor, setClickHistoryAnchor] = useState(null);
  
  // Team lead specific state
  const [teamHelpRequests, setTeamHelpRequests] = useState(0);
  
  useEffect(() => {
    // Enable click tracking
    clickTracker.enable();
    
    // Subscribe to click updates
    const unsubscribe = clickTracker.subscribe((clicks) => {
      setClickCount(clicks.length);
    });
    
    // Cleanup
    return () => {
      unsubscribe();
      clickTracker.disable();
    };
  }, []);
  
  // Simulate team help requests for team lead role
  useEffect(() => {
    if (currentRole === 'team_lead') {
      // Simulate 2 pending help requests when coordinator requests help
      if (helpRequestStatus === 'pending') {
        setTeamHelpRequests(2);
      } else if (helpRequestStatus === 'acknowledged') {
        setTeamHelpRequests(1); // One still pending
      }
    }
  }, [currentRole, helpRequestStatus]);
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleRequestHelp = () => {
    if (!helpRequestStatus) {
      setHelpRequestStatus('pending');
      setTimeout(() => setHelpRequestStatus('acknowledged'), 3000);
    }
  };

  const handleOpenUpdates = (event) => {
    setUpdatesAnchor(event.currentTarget);
  };

  const handleReplayTutorial = () => {
    setUpdatesAnchor(null);
    setTimeout(() => {
      tutorialSystem.start();
    }, 300);
  };

  const handleOpenNudges = (event) => {
    setNudgesAnchor(event.currentTarget);
    setShowNudges(true);
  };

  const handleOpenClickHistory = (event) => {
    setClickHistoryAnchor(event.currentTarget);
    setClickHistoryOpen(true);
  };

  const handleOpenRoleMenu = (event) => {
    setRoleMenuAnchor(event.currentTarget);
  };

  const handleRoleChange = (role) => {
    setCurrentRole(role);
    setRoleMenuAnchor(null);
  };

  // Get role display info
  const getRoleInfo = () => {
    switch (currentRole) {
      case 'team_lead':
        return { name: 'Demo Team Lead', label: 'TEAM LEAD', color: 'rgba(228, 165, 39, 1)' };
      case 'admin':
        return { name: 'Jenn (Admin)', label: 'ADMIN', color: 'rgba(211, 100, 126, 1)' };
      default:
        return { name: 'Demo User', label: 'COORDINATOR', color: 'rgba(133, 186, 199, 1)' };
    }
  };

  const roleInfo = getRoleInfo();

  // Simulate nudges appearing based on time
  React.useEffect(() => {
    const count = nudgesSystem.getNudgeCount(sessionTime);
    setNudgesCount(count);
  }, [sessionTime]);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '48px',
        bgcolor: 'rgba(49, 49, 49, 0.26)',
        backdropFilter: 'blur(30px) saturate(150%)',
        WebkitBackdropFilter: 'blur(30px) saturate(150%)',
        borderBottom: '1px solid rgba(133, 186, 199, 0.3)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: '16px',
        zIndex: 9999,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      {/* LEFT: Logo + User Info */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          component="img"
          src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0' y1='0' x2='1' y2='1'%3E%3Cstop offset='0%25' stop-color='%2379B8C9'/%3E%3Cstop offset='100%25' stop-color='%235B9E82'/%3E%3C/linearGradient%3E%3C/defs%3E%3Ccircle cx='50' cy='50' r='45' fill='url(%23g)'/%3E%3Ctext x='50' y='65' font-size='50' fill='white' text-anchor='middle' font-family='Arial' font-weight='bold'%3EP%3C/text%3E%3C/svg%3E"
          sx={{ width: '32px', height: '32px', borderRadius: '6px' }}
        />
        <Tooltip title="Click to switch roles (demo only)">
          <Box
            onClick={handleOpenRoleMenu}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.75,
              bgcolor: `${roleInfo.color.replace('1)', '0.1)')}`,
              border: `1px solid ${roleInfo.color.replace('1)', '0.3)')}`,
              borderRadius: '6px',
              fontSize: '12px',
              color: 'rgba(255, 255, 255, 0.9)',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: `${roleInfo.color.replace('1)', '0.2)')}`,
                transform: 'scale(1.02)',
              },
            }}
          >
            <Box component="span" sx={{ fontWeight: 500 }}>{roleInfo.name}</Box>
            <Box
              component="span"
              sx={{
                px: 1,
                py: 0.25,
                bgcolor: `${roleInfo.color.replace('1)', '0.2)')}`,
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: roleInfo.color,
              }}
            >
              {roleInfo.label}
            </Box>
          </Box>
        </Tooltip>
      </Box>

      {/* CENTER: Action Buttons */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Tooltip title={featuresEnabled.protocolSearch === false ? "Protocol Search Disabled" : "Search Protocols (Ctrl+K, then P)"}>
          <span>
            <IconButton
              onClick={(e) => onOpenProtocolSearch(e.currentTarget)}
              disabled={featuresEnabled.protocolSearch === false}
              sx={{
                bgcolor: 'rgba(91, 158, 130, 0.15)',
                color: 'rgba(91, 158, 130, 1)',
                border: '1px solid rgba(91, 158, 130, 0.4)',
                borderRadius: '6px',
                p: 1,
                '&:hover': {
                  bgcolor: 'rgba(91, 158, 130, 0.25)',
                },
                '&:disabled': {
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  color: 'rgba(255, 255, 255, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <SearchIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={featuresEnabled.docAssistant === false ? "AI Documentation Disabled" : "Documentation Assistant (Ctrl+K, then D)"}>
          <span>
            <IconButton
              id="prism-generate-docs-btn"
              onClick={(e) => onOpenDocAssistant(e.currentTarget)}
              disabled={featuresEnabled.docAssistant === false}
              sx={{
                bgcolor: 'rgba(121, 184, 201, 0.15)',
                color: 'rgba(121, 184, 201, 1)',
                border: '1px solid rgba(121, 184, 201, 0.4)',
                borderRadius: '6px',
                p: 1,
                '&:hover': {
                  bgcolor: 'rgba(121, 184, 201, 0.25)',
                },
                '&:disabled': {
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  color: 'rgba(255, 255, 255, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <DescriptionIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>

        {/* COORDINATOR: Request Help Button */}
        {currentRole === 'coordinator' && (
          <Tooltip title={
            helpRequestStatus === 'pending' ? 'Help Requested (Pending)' :
            helpRequestStatus === 'acknowledged' ? 'Help Acknowledged' :
            'Request Help from Team Lead'
          }>
            <IconButton
              onClick={handleRequestHelp}
              sx={{
                bgcolor: helpRequestStatus ? 'rgba(228, 165, 39, 0.15)' : 'rgba(255, 255, 255, 0.08)',
                color: helpRequestStatus ? 'rgba(228, 165, 39, 1)' : 'rgba(255, 255, 255, 0.6)',
                border: `1px solid ${helpRequestStatus ? 'rgba(228, 165, 39, 0.4)' : 'rgba(255, 255, 255, 0.2)'}`,
                borderRadius: '6px',
                p: 1,
                position: 'relative',
                animation: helpRequestStatus === 'pending' ? 'pulse 2s ease-in-out infinite' : 'none',
                '&:hover': {
                  bgcolor: helpRequestStatus ? 'rgba(228, 165, 39, 0.25)' : 'rgba(255, 255, 255, 0.15)',
                },
              }}
            >
              <HelpOutlineIcon sx={{ fontSize: 18 }} />
              {helpRequestStatus && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -4,
                    right: -4,
                    bgcolor: helpRequestStatus === 'acknowledged' ? '#4caf50' : '#ff9800',
                    color: 'white',
                    borderRadius: '10px',
                    px: 0.75,
                    py: 0.25,
                    fontSize: '9px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.3px',
                  }}
                >
                  {helpRequestStatus === 'acknowledged' ? '✓' : '!'}
                </Box>
              )}
            </IconButton>
          </Tooltip>
        )}

        {/* TEAM LEAD: Team Help Requests Button */}
        {currentRole === 'team_lead' && teamHelpRequests > 0 && (
          <Tooltip title={`${teamHelpRequests} Team Member${teamHelpRequests > 1 ? 's' : ''} Need Help`}>
            <IconButton
              sx={{
                bgcolor: 'rgba(228, 165, 39, 0.15)',
                color: 'rgba(228, 165, 39, 1)',
                border: '1px solid rgba(228, 165, 39, 0.4)',
                borderRadius: '6px',
                p: 1,
                position: 'relative',
                animation: 'pulse 2s ease-in-out infinite',
                '&:hover': {
                  bgcolor: 'rgba(228, 165, 39, 0.25)',
                },
              }}
            >
              <Badge badgeContent={teamHelpRequests} color="warning">
                <HelpOutlineIcon sx={{ fontSize: 18 }} />
              </Badge>
            </IconButton>
          </Tooltip>
        )}

        {/* ADMIN: System Analytics Button */}
        {currentRole === 'admin' && (
          <Tooltip title="System Analytics Dashboard">
            <IconButton
              sx={{
                bgcolor: 'rgba(211, 100, 126, 0.15)',
                color: 'rgba(211, 100, 126, 1)',
                border: '1px solid rgba(211, 100, 126, 0.4)',
                borderRadius: '6px',
                p: 1,
                '&:hover': {
                  bgcolor: 'rgba(211, 100, 126, 0.25)',
                },
              }}
            >
              <Box component="span" className="material-icons" sx={{ fontSize: 18 }}>analytics</Box>
            </IconButton>
          </Tooltip>
        )}

        {/* COORDINATOR: Nudges/Alerts Button */}
        {currentRole === 'coordinator' && nudgesCount > 0 && featuresEnabled.struggleDetection !== false && (
          <Tooltip title="Alerts & Nudges">
            <IconButton
              onClick={handleOpenNudges}
              sx={{
                bgcolor: 'rgba(244, 67, 54, 0.15)',
                color: 'rgba(244, 67, 54, 1)',
                border: '1px solid rgba(244, 67, 54, 0.4)',
                borderRadius: '6px',
                p: 1,
                animation: 'pulse 2s ease-in-out infinite',
                '&:hover': {
                  bgcolor: 'rgba(244, 67, 54, 0.25)',
                },
              }}
            >
              <Badge badgeContent={nudgesCount} color="error">
                <NotificationsIcon sx={{ fontSize: 18 }} />
              </Badge>
            </IconButton>
          </Tooltip>
        )}

        {/* Workflow Tips Button */}
        <Tooltip title="Workflow Tips">
          <IconButton
            onClick={() => {}}
            sx={{
              bgcolor: 'rgba(156, 39, 176, 0.1)',
              color: 'rgba(156, 39, 176, 1)',
              border: '1px solid rgba(156, 39, 176, 0.3)',
              borderRadius: '6px',
              p: 1,
              '&:hover': {
                bgcolor: 'rgba(156, 39, 176, 0.2)',
              },
            }}
          >
            <LightbulbIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        {/* Click Tracking Button */}
        <Tooltip title="Click Tracking History">
          <IconButton
            onClick={handleOpenClickHistory}
            sx={{
              bgcolor: 'rgba(121, 184, 201, 0.1)',
              color: 'rgba(121, 184, 201, 1)',
              border: '1px solid rgba(121, 184, 201, 0.3)',
              borderRadius: '6px',
              p: 1,
              position: 'relative',
              '&:hover': {
                bgcolor: 'rgba(121, 184, 201, 0.2)',
              },
            }}
          >
            <Badge badgeContent={clickCount} color="primary" max={999}>
              <TouchAppIcon sx={{ fontSize: 18 }} />
            </Badge>
          </IconButton>
        </Tooltip>

        {/* Platform Updates Button */}
        <Tooltip title="Platform Updates & Tips">
          <IconButton
            onClick={handleOpenUpdates}
            sx={{
              bgcolor: 'rgba(228, 165, 39, 0.1)',
              color: 'rgba(228, 165, 39, 1)',
              border: '1px solid rgba(228, 165, 39, 0.3)',
              borderRadius: '6px',
              p: 1,
              '&:hover': {
                bgcolor: 'rgba(228, 165, 39, 0.2)',
              },
            }}
          >
            <CampaignIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* RIGHT: Timer + Sections + Controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* COORDINATOR: Sections Visited */}
        {currentRole === 'coordinator' && (
          <Box
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '12px',
              fontWeight: 500,
              px: 1.5,
              py: 0.5,
              bgcolor: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '6px',
            }}
          >
            {sectionsVisited}/8 sections
          </Box>
        )}

        {/* TEAM LEAD: Active Charts */}
        {currentRole === 'team_lead' && (
          <Box
            sx={{
              color: 'rgba(228, 165, 39, 1)',
              fontSize: '12px',
              fontWeight: 500,
              px: 1.5,
              py: 0.5,
              bgcolor: 'rgba(228, 165, 39, 0.1)',
              border: '1px solid rgba(228, 165, 39, 0.3)',
              borderRadius: '6px',
            }}
          >
            3 active charts
          </Box>
        )}

        {/* ADMIN: System Status */}
        {currentRole === 'admin' && (
          <Box
            sx={{
              color: 'rgba(91, 158, 130, 1)',
              fontSize: '12px',
              fontWeight: 500,
              px: 1.5,
              py: 0.5,
              bgcolor: 'rgba(91, 158, 130, 0.1)',
              border: '1px solid rgba(91, 158, 130, 0.3)',
              borderRadius: '6px',
            }}
          >
            12 users online
          </Box>
        )}

        {/* COORDINATOR: Timer */}
        {currentRole === 'coordinator' && (
          <>
            <Box
              sx={{
                color: sessionTime > 600 ? 'rgba(244, 67, 54, 0.9)' : 'rgba(255, 255, 255, 0.7)',
                fontSize: '13px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
              }}
            >
              <Box component="span">{formatTime(sessionTime)}</Box>
            </Box>

            <Tooltip title="Pause timer">
              <IconButton
                sx={{
                  bgcolor: 'transparent',
                  color: 'rgba(255, 255, 255, 0.5)',
                  p: 0.5,
                  '&:hover': {
                    color: 'rgba(255, 255, 255, 0.9)',
                    bgcolor: 'transparent',
                  },
                }}
              >
                <PauseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </>
        )}

        {/* Demo Mode Badge */}
        <Box
          sx={{
            px: 1.5,
            py: 0.5,
            bgcolor: 'rgba(228, 165, 39, 0.15)',
            color: 'rgba(228, 165, 39, 1)',
            border: '1px solid rgba(228, 165, 39, 0.4)',
            borderRadius: '6px',
            fontSize: '11px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          DEMO MODE
        </Box>
      </Box>

      {/* Platform Updates Menu */}
      <Menu
        anchorEl={updatesAnchor}
        open={Boolean(updatesAnchor)}
        onClose={() => setUpdatesAnchor(null)}
        PaperProps={{
          sx: {
            mt: 1,
            width: 320,
            bgcolor: 'rgba(18, 24, 54, 0.98)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(228, 165, 39, 0.3)',
            borderRadius: '12px',
            color: 'white',
          },
        }}
      >
        <Box sx={{ p: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2" fontWeight={600} color="rgba(228, 165, 39, 1)" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CampaignIcon sx={{ fontSize: 18 }} />
            Platform Updates
          </Typography>
          <IconButton 
            size="small" 
            onClick={() => setUpdatesAnchor(null)}
            sx={{ 
              color: 'rgba(255,255,255,0.7)',
              '&:hover': { 
                bgcolor: 'rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.9)',
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        <MenuItem sx={{ py: 1.5, flexDirection: 'column', alignItems: 'flex-start' }}>
          <Typography variant="body2" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box component="span" className="material-icons" sx={{ fontSize: 16, color: '#a286c9' }}>auto_awesome</Box>
            Ghost Path Protocol Guides
          </Typography>
          <Typography variant="caption" color="rgba(255,255,255,0.7)">
            NEW: Interactive guides now point to EMR elements!
          </Typography>
        </MenuItem>
        <MenuItem sx={{ py: 1.5, flexDirection: 'column', alignItems: 'flex-start' }}>
          <Typography variant="body2" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box component="span" className="material-icons" sx={{ fontSize: 16, color: '#e4a527' }}>bolt</Box>
            Keyboard Shortcuts
          </Typography>
          <Typography variant="caption" color="rgba(255,255,255,0.7)">
            Press Ctrl+K for quick commands (P=Protocols, D=Docs)
          </Typography>
        </MenuItem>
        <MenuItem 
          onClick={handleReplayTutorial}
          sx={{ 
            py: 1.5, 
            flexDirection: 'column', 
            alignItems: 'flex-start',
            bgcolor: 'rgba(228, 165, 39, 0.1)',
            '&:hover': {
              bgcolor: 'rgba(228, 165, 39, 0.2)',
            }
          }}
        >
          <Typography variant="body2" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box component="span" className="material-icons" sx={{ fontSize: 16, color: '#65a9c4' }}>school</Box>
            Replay Interactive Tutorial
          </Typography>
          <Typography variant="caption" color="rgba(255,255,255,0.7)">
            Take the 60-second product tour again
          </Typography>
        </MenuItem>
      </Menu>

      {/* Nudges Menu */}
      <Menu
        anchorEl={nudgesAnchor}
        open={Boolean(nudgesAnchor)}
        onClose={() => setNudgesAnchor(null)}
        PaperProps={{
          sx: {
            mt: 1,
            width: 380,
            bgcolor: 'rgba(18, 24, 54, 0.98)',
            backdropFilter: 'blur(20px)',
            border: '2px solid rgba(244, 67, 54, 0.5)',
            borderRadius: '12px',
            color: 'white',
            animation: 'pulse 2s ease-in-out infinite',
          },
        }}
      >
        <Box sx={{ p: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2" fontWeight={600} color="rgba(244, 67, 54, 1)" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationsIcon sx={{ fontSize: 18 }} />
            Alerts & Reminders
          </Typography>
          <IconButton 
            size="small" 
            onClick={() => setNudgesAnchor(null)}
            sx={{ 
              color: 'rgba(255,255,255,0.7)',
              '&:hover': { 
                bgcolor: 'rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.9)',
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        {sessionTime >= 300 && (
          <MenuItem sx={{ py: 1.5, flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography variant="body2" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box component="span" className="material-icons" sx={{ fontSize: 16, color: '#e4a527' }}>schedule</Box>
              5 Minutes Elapsed
            </Typography>
            <Typography variant="caption" color="rgba(255,255,255,0.7)">
              You've been on this chart for 5 minutes. Consider using AI Documentation to speed things up.
            </Typography>
          </MenuItem>
        )}
        {sessionTime >= 600 && (
          <MenuItem sx={{ py: 1.5, flexDirection: 'column', alignItems: 'flex-start', bgcolor: 'rgba(244, 67, 54, 0.1)' }}>
            <Typography variant="body2" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box component="span" className="material-icons" sx={{ fontSize: 16, color: '#d3647e' }}>warning</Box>
              10 Minutes Elapsed
            </Typography>
            <Typography variant="caption" color="rgba(255,255,255,0.7)">
              Chart time is high. Check if you've completed all {sectionsVisited}/8 sections. Need help?
            </Typography>
          </MenuItem>
        )}
        {sessionTime >= 900 && (
          <MenuItem sx={{ py: 1.5, flexDirection: 'column', alignItems: 'flex-start', bgcolor: 'rgba(244, 67, 54, 0.15)' }}>
            <Typography variant="body2" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box component="span" className="material-icons" sx={{ fontSize: 16, color: '#f44336' }}>error</Box>
              15 Minutes - Action Required
            </Typography>
            <Typography variant="caption" color="rgba(255,255,255,0.7)">
              This chart has exceeded target time. Request help from your team lead if you're stuck.
            </Typography>
          </MenuItem>
        )}
      </Menu>

      {/* Click History Modal */}
      <PrismClickHistoryModal 
        anchorEl={clickHistoryAnchor}
        open={clickHistoryOpen} 
        onClose={() => setClickHistoryOpen(false)} 
      />

      {/* Role Switcher Menu */}
      <Menu
        anchorEl={roleMenuAnchor}
        open={Boolean(roleMenuAnchor)}
        onClose={() => setRoleMenuAnchor(null)}
        PaperProps={{
          sx: {
            mt: 1,
            width: 280,
            bgcolor: 'rgba(18, 24, 54, 0.98)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(133, 186, 199, 0.3)',
            borderRadius: '12px',
            color: 'white',
          },
        }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="subtitle2" fontWeight={600} color="rgba(133, 186, 199, 1)">
            Switch Demo Role
          </Typography>
          <Typography variant="caption" color="rgba(255,255,255,0.6)">
            See how PRISM looks for different roles
          </Typography>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        
        <MenuItem 
          onClick={() => handleRoleChange('coordinator')}
          sx={{ 
            py: 1.5,
            bgcolor: currentRole === 'coordinator' ? 'rgba(133, 186, 199, 0.15)' : 'transparent',
            '&:hover': {
              bgcolor: 'rgba(133, 186, 199, 0.1)',
            }
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="body2" fontWeight={600} color="rgba(133, 186, 199, 1)">
              Care Coordinator
            </Typography>
            <Typography variant="caption" color="rgba(255,255,255,0.7)">
              Front-line charting with nudges and help requests
            </Typography>
          </Box>
        </MenuItem>

        <MenuItem 
          onClick={() => handleRoleChange('team_lead')}
          sx={{ 
            py: 1.5,
            bgcolor: currentRole === 'team_lead' ? 'rgba(228, 165, 39, 0.15)' : 'transparent',
            '&:hover': {
              bgcolor: 'rgba(228, 165, 39, 0.1)',
            }
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="body2" fontWeight={600} color="rgba(228, 165, 39, 1)">
              Team Lead
            </Typography>
            <Typography variant="caption" color="rgba(255,255,255,0.7)">
              Receives help requests from coordinators on your team
            </Typography>
          </Box>
        </MenuItem>

        <MenuItem 
          onClick={() => handleRoleChange('admin')}
          sx={{ 
            py: 1.5,
            bgcolor: currentRole === 'admin' ? 'rgba(211, 100, 126, 0.15)' : 'transparent',
            '&:hover': {
              bgcolor: 'rgba(211, 100, 126, 0.1)',
            }
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="body2" fontWeight={600} color="rgba(211, 100, 126, 1)">
              Platform Admin
            </Typography>
            <Typography variant="caption" color="rgba(255,255,255,0.7)">
              System analytics and platform-wide insights
            </Typography>
          </Box>
        </MenuItem>
      </Menu>

      {/* Add pulse animation */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { 
              box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.4);
            }
            50% { 
              box-shadow: 0 0 0 8px rgba(244, 67, 54, 0);
            }
          }
        `}
      </style>
    </Box>
  );
}
